import React, { useLayoutEffect, useRef, useState } from 'react'
import { AndroidMockup, IPhoneMockup } from 'react-device-mockup'
import { REGISTRATION_REMINDER_DEFAULT_HEADING } from '@ivy-monorepo/shared'
import './NotificationDevicePreview.scss'

/**
 * Logical (point) sizes from Apple HIG / ios-resolution.com.
 * react-device-mockup uses 19.5:9 for notch/island and 16:9 for SE —
 * that matches XS (375×812) and is within 3pt of iPhone 15/17 height.
 */
type ScreenType = 'legacy' | 'notch' | 'island'

type DeviceSpec = {
  id: string
  label: string
  platform: 'ios' | 'android'
  screenType?: ScreenType
  logicalWidth: number
  logicalHeight: number
  inches: string
  cutout: string
}

const DEVICES: DeviceSpec[] = [
  {
    id: 'iphone-se',
    label: 'iPhone SE',
    platform: 'ios',
    screenType: 'legacy',
    logicalWidth: 375,
    logicalHeight: 667,
    inches: '4.7"',
    cutout: 'home button',
  },
  {
    id: 'iphone-xs',
    label: 'iPhone XS',
    platform: 'ios',
    screenType: 'notch',
    logicalWidth: 375,
    logicalHeight: 812,
    inches: '5.8"',
    cutout: 'notch',
  },
  {
    id: 'iphone-15',
    label: 'iPhone 15',
    platform: 'ios',
    screenType: 'island',
    logicalWidth: 393,
    logicalHeight: 852,
    inches: '6.1"',
    cutout: 'Dynamic Island',
  },
  {
    id: 'iphone-17',
    label: 'iPhone 17',
    platform: 'ios',
    screenType: 'island',
    logicalWidth: 402,
    logicalHeight: 874,
    inches: '6.3"',
    cutout: 'Dynamic Island',
  },
  {
    id: 'pixel-8',
    label: 'Pixel 8',
    platform: 'android',
    logicalWidth: 412,
    logicalHeight: 915,
    inches: '6.2"',
    cutout: 'punch-hole',
  },
]

type PreviewMode = 'lock' | 'banner' | 'expanded'

/** Collapsed line counts the OS actually shows before the user expands. */
const BODY_LINES: Record<PreviewMode, { ios: number; android: number }> = {
  lock: { ios: 2, android: 1 },
  banner: { ios: 2, android: 1 },
  expanded: { ios: 8, android: 8 },
}

const MIN_SCALE = 0.4
const MAX_SCALE = 0.8
const DEFAULT_SCALE = 0.52

type NotificationDevicePreviewProps = {
  message: string
  heading?: string
}

const NotificationDevicePreview = ({
  message,
  heading = REGISTRATION_REMINDER_DEFAULT_HEADING,
}: NotificationDevicePreviewProps) => {
  const [scale, setScale] = useState(DEFAULT_SCALE)
  const [mode, setMode] = useState<PreviewMode>('lock')
  const body = message.trim() || 'Your notification text will appear here.'

  return (
    <div className="notification-device-preview">
      <h3 className="notification-device-preview__title">Device preview</h3>
      <p className="notification-device-preview__hint">
        Frames are scaled from real logical sizes (pt). iOS 16+ lock screen uses a bottom stack (~2
        body lines). Unlocked banners are also 2 lines. Android collapsed lock/shade is 1 line unless
        expanded. Narrower phones (SE/XS 375pt) clip earlier than iPhone 17 (402pt) or Pixel 8 (412pt).
      </p>

      <div className="notification-device-preview__controls">
        <div className="notification-device-preview__modes" role="tablist" aria-label="Notification view">
          {(['lock', 'banner', 'expanded'] as PreviewMode[]).map(value => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={mode === value}
              className={
                mode === value
                  ? 'notification-device-preview__mode is-active'
                  : 'notification-device-preview__mode'
              }
              onClick={() => setMode(value)}
            >
              {value === 'lock' ? 'Lock screen' : value === 'banner' ? 'Banner' : 'Expanded'}
            </button>
          ))}
        </div>
        <label className="notification-device-preview__scale">
          <span>
            Scale {Math.round(scale * 100)}%
            <span className="notification-device-preview__scale-unit">
              {' '}
              (1pt → {scale.toFixed(2)}px)
            </span>
          </span>
          <input
            type="range"
            min={MIN_SCALE}
            max={MAX_SCALE}
            step={0.01}
            value={scale}
            onChange={event => setScale(Number(event.target.value))}
          />
        </label>
        <button
          type="button"
          className="link-button"
          style={{ marginLeft: 0 }}
          onClick={() => setScale(DEFAULT_SCALE)}
        >
          Reset size
        </button>
      </div>

      <div className="notification-device-preview__devices">
        {DEVICES.map(device => (
          <DevicePreview
            key={device.id}
            device={device}
            heading={heading}
            message={body}
            mode={mode}
            scale={scale}
          />
        ))}
      </div>
    </div>
  )
}

const DevicePreview = ({
  device,
  heading,
  message,
  mode,
  scale,
}: {
  device: DeviceSpec
  heading: string
  message: string
  mode: PreviewMode
  scale: number
}) => {
  const [truncated, setTruncated] = useState(false)
  const screenWidth = Math.max(120, Math.round(device.logicalWidth * scale))
  const lines = BODY_LINES[mode][device.platform]
  const cssVars = { ['--pt' as string]: `${scale}px` }

  return (
    <div className="notification-device-preview__device">
      <div className="notification-device-preview__label">{device.label}</div>
      <div className="notification-device-preview__spec">
        {device.logicalWidth}×{device.logicalHeight} pt · {device.inches} · {device.cutout}
      </div>
      {device.platform === 'android' ? (
        <AndroidMockup
          screenWidth={screenWidth}
          frameColor="#1c1c1e"
          statusbarColor="#111111"
          navBarColor="#111111"
          hideStatusBar
          transparentNavBar
          navBar="swipe"
        >
          <div className="notif-lock-fill" style={cssVars}>
            <AndroidLockScreen
              heading={heading}
              message={message}
              mode={mode}
              lines={lines}
              onTruncated={setTruncated}
            />
          </div>
        </AndroidMockup>
      ) : (
        <IPhoneMockup
          screenWidth={screenWidth}
          screenType={device.screenType}
          frameColor="#1c1c1e"
          statusbarColor="#111111"
          hideStatusBar
          hideNavBar={device.screenType !== 'legacy'}
          transparentNavBar
        >
          <div className="notif-lock-fill" style={cssVars}>
            <IosLockScreen
              heading={heading}
              message={message}
              mode={mode}
              lines={lines}
              onTruncated={setTruncated}
            />
          </div>
        </IPhoneMockup>
      )}
      <div className="notification-device-preview__meta">
        <span>
          {lines} line{lines === 1 ? '' : 's'}
        </span>
        <span
          className={
            truncated
              ? 'notification-device-preview__clip is-clipped'
              : 'notification-device-preview__clip'
          }
        >
          {truncated ? 'Truncated' : 'Fits'}
        </span>
      </div>
    </div>
  )
}

const iosDate = new Date().toLocaleDateString('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
})

const androidDate = new Date().toLocaleDateString('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
})

const IosLockScreen = ({
  heading,
  message,
  mode,
  lines,
  onTruncated,
}: {
  heading: string
  message: string
  mode: PreviewMode
  lines: number
  onTruncated: (truncated: boolean) => void
}) => (
  <div className={`notif-lock notif-lock--ios notif-lock--${mode}`}>
    {mode !== 'banner' ? (
      <>
        <div className="notif-lock__time">9:41</div>
        <div className="notif-lock__date">{iosDate}</div>
      </>
    ) : (
      <div className="notif-lock__banner-spacer" />
    )}
    <div className="notif-lock__card notif-lock__card--ios">
      <div className="notif-lock__app-row">
        <span className="notif-lock__icon notif-lock__icon--ios">I</span>
        <span className="notif-lock__app">{heading}</span>
        <span className="notif-lock__now">now</span>
      </div>
      <TruncatingText text={message} lines={lines} onTruncated={onTruncated} />
    </div>
  </div>
)

const AndroidLockScreen = ({
  heading,
  message,
  mode,
  lines,
  onTruncated,
}: {
  heading: string
  message: string
  mode: PreviewMode
  lines: number
  onTruncated: (truncated: boolean) => void
}) => (
  <div className={`notif-lock notif-lock--android notif-lock--${mode}`}>
    {mode !== 'banner' ? (
      <>
        <div className="notif-lock__android-status">9:41</div>
        <div className="notif-lock__time notif-lock__time--android">9:41</div>
        <div className="notif-lock__date">{androidDate}</div>
      </>
    ) : (
      <div className="notif-lock__banner-spacer" />
    )}
    <div className="notif-lock__card notif-lock__card--android">
      <div className="notif-lock__app-row">
        <span className="notif-lock__icon notif-lock__icon--android">I</span>
        <div className="notif-lock__android-copy">
          <div className="notif-lock__app">{heading}</div>
          <TruncatingText text={message} lines={lines} onTruncated={onTruncated} />
        </div>
      </div>
    </div>
  </div>
)

const TruncatingText = ({
  text,
  lines,
  onTruncated,
}: {
  text: string
  lines: number
  onTruncated: (truncated: boolean) => void
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) {
      return
    }

    const update = () => {
      onTruncated(el.scrollHeight > el.clientHeight + 1)
    }

    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [text, lines, onTruncated])

  return (
    <div
      ref={ref}
      className="notif-lock__body"
      style={{ WebkitLineClamp: lines, lineClamp: lines } as React.CSSProperties}
    >
      {text}
    </div>
  )
}

export default NotificationDevicePreview
